import React, { useRef } from 'react';
import t from 'react-translate';
import moment from 'moment';
import { config } from '@config/pendo.config.json';

import { css } from '@emotion/react';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { white } from 'styles/global_defaults/colors';

import AthenaTextInput from 'athena/components/text-input';
import AthenaTextarea from 'athena/components/textarea';
import NvDatePicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';

import useDatePickerFocusHandler from 'shared/hooks/use-datepicker-focus-handler';
import { CloneMentorshipProgramActionType } from '../state/types';
import { useCloneMentorshipProgram } from '../state/context';
import { DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from '../../mentoring-program/constants';

const styles = css`
  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: ${standardSpacing}px;
  }

  .form-group {
    .react-datepicker-wrapper {
      width: 100%;
      input {
        border-radius: 5px;
      }
    }
  }
  .bs4-form-control {
    background-color: ${white};
  }
`;

const CloneMentorshipProgramForm = () => {
  const { state, dispatch } = useCloneMentorshipProgram();
  const { targetConfig, errors } = state;
  const dataQATag = config.pendo.athena.mentorshipProgram.cloneProgramModal;
  const translationBase = t.MENTORING_PROGRAMS.PROGRAM_HEADER_MENU.CLONE.STEPS.STEP_ONE;
  const datepickerRef = useRef(null);
  useDatePickerFocusHandler(datepickerRef);

  const nameError = targetConfig.name.trim().length === 0
    ? t.VALIDATION.REQUIRED()
    : '';

  const currentErrors = {
    ...errors,
    name: targetConfig.name.trim().length === 0
      ? nameError
      : errors.name,
  };

  const handleChange = (prop: string) => (
    e: moment.Moment | null | string | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let value: string | null;

    if (moment.isMoment(e)) {
      value = e.toISOString();
    } else if (e === null) {
      value = null;
    } else if (typeof e === 'string') {
      value = e;
    } else {
      ({ value } = e.target);
    }

    dispatch({
      type: CloneMentorshipProgramActionType.UPDATE_TARGET_CONFIG,
      payload: { [prop]: value },
    });

    if (errors[prop]) {
      dispatch({
        type: CloneMentorshipProgramActionType.SET_ERRORS,
        payload: { ...errors, [prop]: '' },
      });
    }
  };

  const handleBlur = (prop: string) => () => {
    if (prop === 'name') {
      dispatch({
        type: CloneMentorshipProgramActionType.SET_ERRORS,
        payload: { ...errors, name: nameError },
      });
    }
  };

  return (
    <div css={styles} className='new-program-form bg-gray-7 p-4'>
      <div className='heading-5 mb-4'>
        {translationBase.FORM.NEW_PROGRAM()}
      </div>

      <div className='form-grid'>
        <div className='form-group d-flex flex-column'>
          <AthenaTextInput
            id='clone-name'
            value={targetConfig.name}
            label={translationBase.INFO.PROGRAM_NAME()}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            error={currentErrors.name}
            required
            maxLength={NAME_MAX_LENGTH}
            dataQa={dataQATag.nameInput}
            autoComplete='off'
          />
        </div>

        <div className='form-group d-flex flex-column w-100' ref={datepickerRef}>
          <label htmlFor='clone-release-date'>
            {translationBase.INFO.RELEASE_DATE()} ({translationBase.FORM.OPTIONAL()})
          </label>
          <NvDatePicker
            type={DatePickerType.DATETIME}
            value={targetConfig.releasedDate ? moment(targetConfig.releasedDate) : undefined}
            onChange={handleChange('releasedDate')}
            inputProps={{
              id: 'clone-release-date',
            }}
          />
        </div>
      </div>

      <div className='form-group'>
        <AthenaTextarea
          id='clone-description'
          rows={4}
          label={t.MENTORING_PROGRAMS.PROGRAM_HEADER_MENU.CLONE.STEPS.STEP_ONE.FORM.PROGRAM_DESCRIPTION()}
          value={targetConfig.description}
          onChange={handleChange('description')}
          maxLength={DESCRIPTION_MAX_LENGTH}
          dataQa={dataQATag.descriptionInput}
        />
      </div>
    </div>
  );
};

export default CloneMentorshipProgramForm;
